/**
 * Operations (service-request / task) mutations.
 *
 * Escalation is NON-OPTIMISTIC by policy. `createServiceRequest` and
 * `updateTaskStatus` are also kept non-optimistic: task status drives SLA
 * timers, so we wait for the authoritative server result and then sync the
 * detail + list caches.
 */

import { type UseMutationResult, useMutation, useQueryClient } from '@tanstack/react-query';
import { unwrap } from '../queries/_helpers';
import { queryKeys } from '../query-keys';
import { useApiClient } from '../react/ApiClientProvider';
import { type ServiceRequest, serviceRequestSchema } from '../responses';
import type { CreateServiceRequestBody, EscalateTaskBody, UpdateTaskStatusBody } from './_types';

function useTaskSync(tenantId: string, hotelId: string) {
  const queryClient = useQueryClient();
  return (task: ServiceRequest) => {
    queryClient.setQueryData(queryKeys.task(tenantId, hotelId, task.id), task);
    void queryClient.invalidateQueries({ queryKey: queryKeys.tasks(tenantId, hotelId) });
    void queryClient.invalidateQueries({ queryKey: queryKeys.taskExceptions(tenantId, hotelId) });
  };
}

/** `POST /operations/service-requests` — create a service request (task). */
export function useCreateServiceRequest(
  tenantId: string,
  hotelId: string,
): UseMutationResult<ServiceRequest, unknown, CreateServiceRequestBody> {
  const client = useApiClient();
  const sync = useTaskSync(tenantId, hotelId);
  return useMutation({
    mutationFn: async (body: CreateServiceRequestBody) => {
      const result = await client.POST(
        '/api/v1/tenants/{tenantId}/hotels/{hotelId}/operations/service-requests',
        { params: { path: { tenantId, hotelId } }, body, parseAs: 'json' },
      );
      return unwrap(result, serviceRequestSchema);
    },
    onSuccess: sync,
  });
}

export interface UpdateTaskStatusVars {
  requestId: string;
  body: UpdateTaskStatusBody;
}

/** `POST /operations/service-requests/:requestId/status` — move a task's status. */
export function useUpdateTaskStatus(
  tenantId: string,
  hotelId: string,
): UseMutationResult<ServiceRequest, unknown, UpdateTaskStatusVars> {
  const client = useApiClient();
  const sync = useTaskSync(tenantId, hotelId);
  return useMutation({
    mutationFn: async ({ requestId, body }: UpdateTaskStatusVars) => {
      const result = await client.POST(
        '/api/v1/tenants/{tenantId}/hotels/{hotelId}/operations/service-requests/{requestId}/status',
        { params: { path: { tenantId, hotelId, requestId } }, body, parseAs: 'json' },
      );
      return unwrap(result, serviceRequestSchema);
    },
    onSuccess: sync,
  });
}

export interface EscalateTaskVars {
  requestId: string;
  body?: EscalateTaskBody;
}

/**
 * `POST /operations/service-requests/:requestId/escalate`.
 *
 * NON-OPTIMISTIC (escalation routing) — wait for the backend, then sync.
 */
export function useEscalateTask(
  tenantId: string,
  hotelId: string,
): UseMutationResult<ServiceRequest, unknown, EscalateTaskVars> {
  const client = useApiClient();
  const sync = useTaskSync(tenantId, hotelId);
  return useMutation({
    mutationFn: async ({ requestId, body }: EscalateTaskVars) => {
      const result = await client.POST(
        '/api/v1/tenants/{tenantId}/hotels/{hotelId}/operations/service-requests/{requestId}/escalate',
        { params: { path: { tenantId, hotelId, requestId } }, body: body ?? {}, parseAs: 'json' },
      );
      return unwrap(result, serviceRequestSchema);
    },
    onSuccess: sync,
  });
}
